import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link
} from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import styled, { keyframes, css } from 'styled-components';
import { Client, Wallet, xrpToDrops } from 'xrpl';
import { LoadingOverlay } from './components/LoadingOverlay';
import { WebhookManager } from './services/WebhookManager';
import { TomlGenerator } from './services/TomlGenerator';
// Remove HostingGuideModal import
import { WalletModal } from './components/WalletModal';
import { GlobalStyle, theme } from './styles/GlobalStyle';
import { validateMnemonic, mnemonicToSeed } from '@scure/bip39';
import { HDKey } from '@scure/bip32';
import TokenFeed from './components/TokenFeed';
import TokenMetadataUploader from './components/TokenMetadataUploader';
import tokenUploadService from './services/TokenUploadService';
import * as xrpl from 'xrpl';
import { ThemeProvider, ThemeContext } from './contexts/ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import { XrplClient } from './components/XrplClient';

// Theme-aware GlobalStyle wrapper
const GlobalStyleWithTheme = () => {
  const { theme } = useContext(ThemeContext);
  return <GlobalStyle theme={theme} />;
};

// Configurable fee settings
const CREATOR_FEE = 0; // Your fee in XRP
const ACCOUNT_RESERVE = 1; // Base reserve per account
const TRUSTLINE_RESERVE = 0.2; // Reserve per trustline
const AMM_POOL_RESERVE = 2; // Reserve for AMM pool

// eslint-disable-next-line no-unused-vars
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// eslint-disable-next-line no-unused-vars
const slideInFromBottom = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// eslint-disable-next-line no-unused-vars
const shimmerAnimation = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// eslint-disable-next-line no-unused-vars
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); filter: brightness(1.1); }
  100% { transform: scale(1); }
`;

// eslint-disable-next-line no-unused-vars
const tiltAnimation = keyframes`
  0% { transform: perspective(1000px) rotateX(0deg) rotateY(0deg); }
  25% { transform: perspective(1000px) rotateX(1deg) rotateY(1deg); }
  50% { transform: perspective(1000px) rotateX(0deg) rotateY(-1deg); }
  75% { transform: perspective(1000px) rotateX(-1deg) rotateY(0deg); }
  100% { transform: perspective(1000px) rotateX(0deg) rotateY(0deg); }
`;

// eslint-disable-next-line no-unused-vars
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// eslint-disable-next-line no-unused-vars
const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(138, 43, 226, 0.2); }
  50% { box-shadow: 0 0 20px rgba(138, 43, 226, 0.6); }
  100% { box-shadow: 0 0 5px rgba(138, 43, 226, 0.2); }
`;

const wiggle = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(15deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-15deg); }
  100% { transform: rotate(0deg); }
`;

// Add more fun animations
const bounce = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-10px) scale(1.05); }
`;

const pop = keyframes`
  0% { transform: scale(0); }
  70% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const wobble = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.5); }
  50% { opacity: 1; transform: scale(1.2); }
`;

// Main layout components
const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${theme.background};
  overflow-x: hidden; // Prevent horizontal scrolling

  &::before {
    content: '';
    position: fixed;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(63, 140, 255, 0.08) 0%, transparent 70%);
    z-index: 0;
    pointer-events: none; // Ensure this doesn't interfere with clicks
  }
  
  &::after {
    content: '';
    position: fixed;
    bottom: -30%;
    left: -30%;
    width: 80%;
    height: 80%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0, 229, 255, 0.08) 0%, transparent 70%);
    z-index: 0;
    pointer-events: none; // Ensure this doesn't interfere with clicks
  }
`;

const Header = styled.header`
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  border-bottom: 8px solid ${theme.primary};
  border-radius: 0 0 30px 30px;
  position: relative;
  z-index: 100;
  margin: 0 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 20px;
    width: 30px;
    height: 30px;
    background: ${theme.accent};
    border-radius: 50%;
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 40px;
    width: 20px;
    height: 20px;
    background: ${theme.secondary};
    border-radius: 50%;
    z-index: -1;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  h1 {
    font-family: 'Fredoka', sans-serif;
    font-size: 3.5rem;
    font-weight: 700;
    margin: 0;
    color: ${theme.primary};
    text-shadow: 4px 4px 0 ${theme.accent}40, 
                8px 8px 0 rgba(0, 0, 0, 0.1);
    letter-spacing: -0.03em;
    position: relative;
    transform-origin: center left;
    transform: rotate(-2deg);
    animation: ${wobble} 6s ease-in-out infinite;
    
    span {
      display: inline-block;
      color: ${theme.accent};
      animation: ${wiggle} 3s ease-in-out infinite;
      transform-origin: bottom center;
      font-size: 4rem;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 6px;
      background: linear-gradient(90deg, ${theme.primary}, ${theme.secondary});
      border-radius: 10px;
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  padding: 1.5rem;
  max-width: 2000px;
  margin: 0 auto;
  width: 100%;
  gap: 1.5rem;
  position: relative; // Ensure proper stacking context
  z-index: 1; // Place above background decorations
  
  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Sidebar = styled.div`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 1024px) {
    width: 100%;
    order: 2; /* Move sidebar below main content on smaller screens */
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 1rem;
  
  @media (max-width: 768px) {
    order: 1;  // Make main content appear first on mobile
    padding: 0.75rem;
  }
`;

// Card components
const Card = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 8px 25px rgba(63, 140, 255, 0.1);
  border: 3px solid ${theme.borderLight};
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(63, 140, 255, 0.15);
  }

  ${props => props.interactive && `
    cursor: pointer;
    
    &:active {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(63, 140, 255, 0.1);
    }
  `}
`;

const CardTitle = styled.h3`
  position: relative;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  color: ${theme.text};
  font-weight: 700;
  letter-spacing: -0.02em;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: ${theme.secondary};
    border-radius: 3px;
  }
`;

// Form components
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Update the FormSection with cartoonish style
const FormSection = styled(Card)`
  margin-bottom: 2rem;
  padding: 2.5rem;
  border-radius: 30px;
  box-shadow: 0 15px 40px rgba(63, 140, 255, 0.15);
  background: white;
  border: 4px solid ${theme.borderLight};
  position: relative;
  animation: ${fadeIn} 0.5s forwards;
  animation-delay: ${props => props.delay || '0s'};
  transform: rotate(0deg);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 45px rgba(63, 140, 255, 0.25);
    border-color: ${theme.primary}50;
  }
  
  &::before {
    content: ${props => props.icon || "'🔮'"};
    position: absolute;
    top: -15px;
    right: -10px;
    font-size: 2rem;
    background: white;
    border-radius: 50%;
    padding: 5px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    animation: ${bounce} 3s infinite;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
  font-weight: 600;
    font-size: 0.95rem;
    color: ${theme.textSecondary};
  }
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid ${theme.borderLight};
  color: ${theme.text};
  border-radius: 12px;
  padding: 0.75rem 1rem;
  margin-bottom: 0;
  box-shadow: inset 0 2px 5px rgba(13, 71, 161, 0.05);
  transition: all 0.3s ease;
  font-family: 'Quicksand', sans-serif;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(13, 71, 161, 0.1),
                inset 0 2px 5px rgba(13, 71, 161, 0.05);
  }
  
  &::placeholder {
    color: ${theme.textMuted};
    opacity: 0.7;
  }
`;

const Slider = styled.input`
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  background: ${theme.borderLight};
  border-radius: 10px;
  outline: none;
  padding: 0;
  margin: 10px 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${theme.primary};
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 0 10px rgba(13, 71, 161, 0.3);
    border: 3px solid white;
    
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 0 15px rgba(13, 71, 161, 0.4);
    }
  }
  
  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: ${theme.primary};
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 0 10px rgba(13, 71, 161, 0.3);
    border: 3px solid white;
  
  &:hover {
      transform: scale(1.1);
      box-shadow: 0 0 15px rgba(13, 71, 161, 0.4);
    }
  }
`;

// Button components
const Button = styled.button`
  background: ${props => props.primary ? 
    `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` : 
    'white'};
  color: ${props => props.primary ? 'white' : theme.primary};
  border: ${props => props.primary ? 'none' : `4px solid ${theme.primary}`};
  padding: ${props => props.large ? '1.2rem 2rem' : '0.7rem 1.5rem'};
  border-radius: 50px;
  font-weight: 800;
  font-size: ${props => props.large ? '1.5rem' : '1.1rem'};
  cursor: pointer;
  font-family: 'Fredoka', sans-serif;
  position: relative;
  overflow: hidden;
  transform: rotate(0deg);
  box-shadow: ${props => props.primary ? 
    '0 10px 20px rgba(0, 0, 0, 0.15), 0 0 0 5px rgba(255,255,255,0.7) inset' : 
    '0 5px 15px rgba(0, 0, 0, 0.1)'};
  transition: all 0.3s cubic-bezier(0.68, -0.6, 0.32, 1.6);
  margin: 0.3rem;
  
  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: ${props => props.primary ? 
      '0 15px 30px rgba(0, 0, 0, 0.2), 0 0 0 5px rgba(255,255,255,0.7) inset' : 
      '0 10px 20px rgba(0, 0, 0, 0.15)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${theme.accent};
    opacity: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    right: -5px;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: ${theme.secondary};
    opacity: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  overflow: visible;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: inline-block;
  overflow: visible;
  padding: 0.5rem;
  position: relative;
`;

// Wallet components
const WalletManagementCard = styled.div`
  margin-bottom: 0;
  overflow: visible;
  padding: 1.5rem;
  
  .wallet-header {
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;
    
    h3 {
      margin-bottom: 1rem;
    }

    ${ButtonGroup} {
      justify-content: space-between;
      flex-wrap: wrap;
    }
  }
  
  .wallet-instructions {
    background: ${theme.backgroundAlt};
    padding: 1rem;
    border-radius: 12px;
    margin-bottom: 1.5rem;
    border: 1px dashed ${theme.borderLight};
    
    p {
      color: ${theme.textSecondary};
      font-size: 0.9rem;
      margin: 0;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 2rem 1rem;
    color: ${theme.textSecondary};
    
    svg {
      font-size: 2.5rem;
      margin-bottom: 1rem;
      color: ${theme.primary};
      opacity: 0.5;
    }
    
    h4 {
      color: ${theme.textPrimary};
      margin-bottom: 0.5rem;
    }
    
    p {
      margin-bottom: 1.5rem;
      font-size: 0.9rem;
    }
  }
`;

const WalletCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid ${props => props.active ? theme.primary : theme.borderLight};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: ${props => props.active 
    ? `0 7px 25px rgba(13, 71, 161, 0.15)` 
    : `0 5px 15px rgba(13, 71, 161, 0.08)`};
  
  ${props => props.active && `
    background: linear-gradient(45deg, rgba(13, 71, 161, 0.03), rgba(0, 200, 83, 0.03));
  `}

  &:hover {
    border-color: ${theme.primary};
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(13, 71, 161, 0.12);
  }
  
  .wallet-badge {
    position: absolute;
    top: -10px;
    right: 20px;
    background: ${props => props.active ? theme.primary : '#e0e0e0'};
    color: ${props => props.active ? 'white' : '#666'};
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border: 2px solid white;
  }
  
  .wallet-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid ${theme.borderLight};
    flex-wrap: wrap;
    gap: 0.5rem;
    
    ${Button} {
      flex: 1;
      min-width: 120px;
      padding: 0.6rem 1rem;
      font-size: 0.9rem;
      transform: none;
      margin: 0;
    }
  }
`;

const WalletInfo = styled.div`
  margin: 0.8rem 0;
  word-break: break-all;
  position: relative;

  label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    font-weight: 600;
    font-size: 0.85rem;
    color: ${theme.textSecondary};
    
    .copy-btn {
      background: none;
      border: none;
      color: ${theme.primary};
      font-size: 0.85rem;
      cursor: pointer;
      padding: 0;
      opacity: 0.7;
      transition: all 0.2s ease;
      
      &:hover {
        opacity: 1;
      }
    }
  }
  
  p {
    color: ${theme.text};
    margin: 0;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem;
    background: #EDF7FD;
    border-radius: 8px;
    border: 1px solid ${theme.borderLight};
    box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.02);
    overflow-x: auto;
  }
  
  .sensitive-data {
    filter: blur(4px);
    transition: filter 0.3s ease;
    
    &:hover {
      filter: blur(0);
    }
  }
`;

// Fix the summary card styling to prevent overflow
const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  padding: 1.5rem;
  border: 2px solid ${theme.borderLight};
  box-shadow: 0 10px 30px rgba(13, 71, 161, 0.08);
  max-width: 100%;
  overflow: hidden;
  height: fit-content;
  
  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    
    h3 {
      margin: 0;
      color: ${theme.text};
  position: relative;
      padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
        bottom: 0;
        left: 0;
        width: 50px;
        height: 3px;
        background: ${theme.secondary};
        border-radius: 3px;
      }
    }
  }
  
  .summary-total {
    background: ${theme.backgroundAlt};
    padding: 1rem;
    border-radius: 12px;
    margin: 1.5rem 0;
    border: 1px solid ${theme.borderLight};
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .total-label {
      font-weight: 600;
      color: ${theme.textSecondary};
    }
    
    .total-value {
      font-size: 1.4rem;
      font-weight: 700;
      color: ${theme.primary};
    }
  }
  
  ${Button} {
    width: 100%;
    margin-top: 1rem;
  }
  
  .connect-wallet-message {
    text-align: center;
    color: ${theme.textSecondary};
    margin-top: 1rem;
      font-size: 0.9rem;
  }
  
  .fee-items {
    max-height: 200px;
    overflow-y: auto;
    padding-right: 10px;
    margin-right: -5px;
    
    &::-webkit-scrollbar {
      width: 5px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${theme.backgroundAlt};
      border-radius: 10px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${theme.borderLight};
      border-radius: 10px;
    }
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.7rem 0;
  border-bottom: 1px solid ${theme.borderLight};
  
  &:last-of-type {
    border-bottom: none;
  }
  
  span:first-child {
    color: ${theme.textSecondary};
    font-size: 0.9rem;
  display: flex;
  align-items: center;
    flex-shrink: 0;
    margin-right: 10px;
    
    .info-tooltip {
      margin-left: 0.4rem;
      color: ${theme.textMuted};
      cursor: help;
      font-size: 0.8rem;
    }
  }
  
  span:last-child {
    font-weight: 600;
    color: ${theme.text};
    text-align: right;
  }
`;

// Modal components for the updated theme
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(13, 71, 161, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  border: 2px solid ${theme.borderLight};
  box-shadow: 0 15px 50px rgba(13, 71, 161, 0.3);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, ${theme.primary}, ${theme.secondary});
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
  }
`;

const ModalTitle = styled.h2`
  color: ${theme.primary};
  margin-bottom: 1.5rem;
  font-family: 'Fredoka', sans-serif;
  position: relative;
  padding-bottom: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: ${theme.secondary};
    border-radius: 3px;
  }
`;

// Define a styled Select component to avoid using inline styles
const StyledSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
    border-radius: 12px;
  border: 2px solid ${theme.borderLight};
  background-color: rgba(255, 255, 255, 0.9);
  color: ${theme.text};
  font-family: 'Quicksand', sans-serif;
  box-shadow: inset 0 2px 5px rgba(13, 71, 161, 0.05);
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(13, 71, 161, 0.1);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

// Update the AdvancedSettingsSection to remove select styling since we moved it to StyledSelect
const AdvancedSettingsSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.borderLight};
  
  .section-title {
    margin-bottom: 0.5rem;
    
    h4 {
      margin: 0;
      color: ${theme.textSecondary};
      font-size: 1rem;
      font-weight: 600;
    }
  }
  
  .wallet-instructions {
    margin-bottom: 1rem;
    
    p {
      color: ${theme.textSecondary};
      font-size: 0.9rem;
      margin: 0;
    }
  }
  
  .advanced-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

// Fix icon upload interactive area
const TokenIconUploader = styled.div`
  position: relative;
  border-radius: 12px;
  border: 2px dashed ${theme.border};
  padding: 1rem;
  text-align: center;
    margin-bottom: 1rem;
  cursor: pointer;
  background-color: rgba(225, 245, 254, 0.5);
  transition: all 0.2s ease;
  z-index: 2; // Ensure clickable area is on top
  
  &:hover {
    background-color: rgba(225, 245, 254, 0.8);
    border-color: ${theme.primary};
  }
  
  img {
    max-width: 100%;
    max-height: 120px;
    border-radius: 8px;
    display: block;
    margin: 0 auto 0.5rem auto;
  }
  
  .icon-preview {
    width: 60px;
    height: 60px;
    background-color: ${theme.backgroundAlt};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem auto;
    font-size: 1.5rem;
    color: ${theme.primary};
  }
  
  p {
    font-size: 0.9rem;
    color: ${theme.textMuted};
      margin: 0;
    }
  
  .url-preview {
    font-size: 0.7rem;
    word-break: break-all;
    color: ${theme.textMuted};
  }
`;

// Update the FormContainer for more cartoonish style
const FormContainer = styled.div`
  background: white;
  border-radius: 30px;
  padding: 2.5rem;
  box-shadow: 0 15px 40px rgba(63, 140, 255, 0.15);
  width: 100%;
  max-width: 800px;
  margin: 2rem auto;
  position: relative;
  border: 4px solid ${theme.borderLight};
  animation: ${pop} 0.5s ease-out;
  transform: rotate(-1deg);
  
  &::before {
    content: '✨';
    position: absolute;
    top: -20px;
    right: -10px;
    font-size: 2rem;
    animation: ${sparkle} 3s infinite;
  }
  
  &::after {
    content: '🎨';
    position: absolute;
    bottom: -20px;
    left: -10px;
    font-size: 2rem;
    animation: ${sparkle} 3s infinite 1s;
  }
`;

const CartoonFormSection = styled.div`
  margin-bottom: 2.5rem;
  position: relative;
  animation: ${fadeIn} 0.5s forwards;
  animation-delay: ${props => props.delay || '0s'};
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${theme.primary};
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  font-family: 'Fredoka', sans-serif;
  transform: rotate(-1deg);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${theme.accent};
    border-radius: 10px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  &:hover label {
    transform: translateY(-5px);
    color: ${theme.primary};
  }
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 0.8rem;
  font-weight: 600;
  font-size: 1.1rem;
  color: ${theme.textPrimary};
  transition: all 0.3s;
  transform-origin: left;
  font-family: 'Quicksand', sans-serif;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.9rem 1.2rem;
  border: 3px solid ${theme.borderLight};
  border-radius: 15px;
  font-size: 1.1rem;
  transition: all 0.3s;
  background: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  font-family: 'Quicksand', sans-serif;
  transform: rotate(1deg);
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 8px 20px rgba(63, 140, 255, 0.15);
    transform: rotate(0) scale(1.02);
  }
  
  &::placeholder {
    color: ${theme.textSecondary}80;
  }
`;

const FormButton = styled.button`
  background: ${props => props.secondary ? 'white' : `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`};
  color: ${props => props.secondary ? theme.primary : 'white'};
  border: ${props => props.secondary ? `3px solid ${theme.primary}` : 'none'};
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.68, -0.6, 0.32, 1.6);
  margin-right: ${props => props.marginRight ? '1rem' : '0'};
  box-shadow: ${props => props.secondary ? '0 5px 15px rgba(0, 0, 0, 0.05)' : '0 10px 25px rgba(63, 140, 255, 0.3)'};
  font-family: 'Fredoka', sans-serif;
  transform: ${props => props.secondary ? 'rotate(-1deg)' : 'rotate(1deg)'};
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px) ${props => props.secondary ? 'rotate(-1deg)' : 'rotate(1deg)'};
    box-shadow: ${props => props.secondary ? 
      '0 8px 20px rgba(0, 0, 0, 0.1)' : 
      '0 15px 30px rgba(63, 140, 255, 0.4)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  
  &:hover::after {
    transform: translateX(100%);
  }
  
  ${props => props.bouncy && css`
    animation: ${bounce} 4s infinite ease-in-out;
  `}
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 0.5rem;
  cursor: pointer;
`;

const SwitchLabel = styled.span`
  margin-left: 0.8rem;
  font-weight: 600;
  font-size: 1.1rem;
  color: ${theme.textPrimary};
  transition: all 0.3s;
  font-family: 'Quicksand', sans-serif;
`;

const Switch = styled.div`
  position: relative;
  width: 60px;
  height: 30px;
  background: ${props => props.checked ? theme.primary : theme.borderLight};
  border-radius: 30px;
  padding: 4px;
  transition: all 0.3s ease;
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.05);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    top: 4px;
    left: ${props => props.checked ? 'calc(100% - 26px)' : '4px'};
    transition: all 0.3s cubic-bezier(0.68, -0.6, 0.32, 1.6);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
`;

const OptionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`;

const OptionCard = styled.div`
  background: white;
  border: ${props => props.selected ? `3px solid ${theme.primary}` : `3px solid ${theme.borderLight}`};
  border-radius: 20px;
  padding: 1.5rem;
  cursor: pointer;
  flex: 1 0 45%;
  min-width: 200px;
  max-width: 300px;
  transition: all 0.3s cubic-bezier(0.68, -0.6, 0.32, 1.6);
  box-shadow: ${props => props.selected ? 
    `0 10px 25px rgba(63, 140, 255, 0.2)` : 
    `0 5px 15px rgba(0, 0, 0, 0.05)`};
  position: relative;
  transform: ${props => props.index % 2 === 0 ? 'rotate(1deg)' : 'rotate(-1deg)'};
  animation: ${pop} 0.5s forwards;
  animation-delay: ${props => props.index * 0.1}s;
  opacity: 0;
  
  &:hover {
    transform: translateY(-5px) ${props => props.index % 2 === 0 ? 'rotate(1deg)' : 'rotate(-1deg)'};
    box-shadow: 0 15px 30px rgba(63, 140, 255, 0.15);
    border-color: ${props => props.selected ? theme.primary : theme.primary + '80'};
  }
  
  &::before {
    content: ${props => props.selected ? "'✓'" : "''"};
    position: absolute;
    top: 10px;
    right: 15px;
    font-size: 1.5rem;
    color: ${theme.primary};
    font-weight: bold;
  }
`;

const OptionTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 0.8rem;
  color: ${theme.textPrimary};
  font-family: 'Fredoka', sans-serif;
`;

const OptionDescription = styled.p`
  color: ${theme.textSecondary};
  font-size: 1rem;
  line-height: 1.5;
  font-family: 'Quicksand', sans-serif;
`;

const FeeContainer = styled.div`
  background: rgba(63, 140, 255, 0.05);
  border-radius: 20px;
  padding: 1.5rem;
  margin-top: 2rem;
  border: 2px dashed ${theme.primary}50;
  animation: ${pulse} 3s infinite;
`;

const FeeItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.8rem;
  font-family: 'Quicksand', sans-serif;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 0.8rem;
    border-top: 2px dashed ${theme.borderLight};
    font-weight: 700;
  }
`;

const FeeLabel = styled.span`
  color: ${theme.textSecondary};
`;

const FeeValue = styled.span`
  color: ${theme.textPrimary};
  font-weight: 600;
`;

const WalletPreviewContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border: 3px solid ${theme.borderLight};
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
  color: ${theme.textPrimary};
  max-height: 200px;
  overflow-y: auto;
  position: relative;
  
  &::before {
    content: '💼';
    position: absolute;
    top: -10px;
    right: -10px;
    font-size: 1.5rem;
    background: white;
    border-radius: 50%;
    padding: 5px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
    animation: ${wobble} 3s ease-in-out infinite;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ActionButton = styled.button`
  background: ${theme.primary};
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 1rem;
  width: 100%;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${theme.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BuyPadButton = styled.a`
  padding: 0.6rem 1.2rem;
  background: linear-gradient(135deg, #6BCDFF, #6DA5FF);
  border-radius: 8px;
  color: white;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  white-space: nowrap;
  text-transform: uppercase;
  box-shadow: 0 4px 6px rgba(109, 165, 255, 0.3);
  border: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 7px 14px rgba(109, 165, 255, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const SocialButtonsContainer = styled.div`
  display: flex;
  position: fixed;
  bottom: 20px;
  right: 20px;
  gap: 10px;
  z-index: 1000;
`;

const SocialButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.bgColor || '#000'};
  color: white;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: white;
  }
`;

// Wallet Balance component
const WalletBalance = ({ address }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        // Use a direct request to fetch account info instead of getXrpBalance
        const client = await XrplClient.getClient();
        
        // Use our own custom logic to get the balance
        const response = await client.request({
          command: "account_info",
          account: address,
          ledger_index: "validated"
        });
        
        // Convert drops to XRP (1 XRP = 1,000,000 drops)
        const balanceInDrops = response.result.account_data.Balance;
        const xrpBalance = (parseInt(balanceInDrops) / 1000000).toFixed(6);
        
        setBalance(xrpBalance);
        setLoading(false);
        await client.disconnect();
      } catch (error) {
        console.error('Error fetching balance:', error);
        // If account not found, show 0 balance instead of "Error"
        if (error.data && error.data.error === 'actNotFound') {
          setBalance('0.000000');
        } else {
          setBalance('Error');
        }
        setLoading(false);
      }
    };

    fetchBalance();
  }, [address]);

  if (loading) return <p style={{ color: theme.textSecondary, fontStyle: 'italic', fontSize: '0.8rem' }}>Loading balance...</p>;
  
  return (
    <div style={{ 
      marginTop: '0.8rem', 
      background: '#E8F5E9', 
      padding: '0.6rem 0.8rem',
      borderRadius: '8px',
      border: '1px solid #C8E6C9',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#2E7D32' }}>Balance:</span>
      <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{balance} XRP</span>
    </div>
  );
};

// Change DEFAULT_DOMAIN to use the domain without www prefix
const DEFAULT_DOMAIN = "xpad.space";

// Add this helper function before the App() function
const setIssuerDomain = async (client, wallet) => {
  try {
    // Use xpad.space without www prefix to match XRPL standard practices
    const domain = "xpad.space"; 
    
    // Convert domain to hex for XRPL
    const domainHex = Buffer.from(domain)
      .toString('hex')
      .toUpperCase();
    
    // Set up the AccountSet transaction
    const accountSetTx = {
      TransactionType: "AccountSet",
      Account: wallet.address,
      Domain: domainHex,
      Fee: "12"
    };
    
    const prepared = await client.autofill(accountSetTx);
    const signed = wallet.sign(prepared);
    const result = await client.submitAndWait(signed.tx_blob);

    if (result.result.meta.TransactionResult !== "tesSUCCESS") {
      throw new Error(`Failed to set domain: ${result.result.meta.TransactionResult}`);
    }
    
    return { 
      success: true, 
      hash: result.result.hash,
      domain: domain,
      domainHex: domainHex
    };
  } catch (error) {
    console.error("Error setting domain:", error);
    throw error;
  }
};

export function App() {
  // Add state for advanced settings visibility
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Load wallets from localStorage on initial render
  const [wallets, setWallets] = useState(() => {
    const savedWallets = localStorage.getItem('wallets');
    if (savedWallets) {
      try {
        // Parse the saved wallets and reinstantiate Wallet objects
        const parsedWallets = JSON.parse(savedWallets);
        return parsedWallets.map(wallet => {
          try {
            return Wallet.fromSeed(wallet.seed);
          } catch (error) {
            console.error('Error recreating wallet from seed:', error);
            return null;
          }
        }).filter(wallet => wallet !== null);
      } catch (error) {
        console.error('Error parsing saved wallets:', error);
        return [];
      }
    }
    return [];
  });
  
  // Load active wallet from localStorage if it exists
  const [activeWallet, setActiveWallet] = useState(() => {
    const activeWalletAddress = localStorage.getItem('activeWalletAddress');
    if (activeWalletAddress && wallets.length > 0) {
      return wallets.find(wallet => wallet.address === activeWalletAddress) || null;
    }
    return null;
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  // Load token data from localStorage on initial render
  const [tokenData, setTokenData] = useState(() => {
    const savedTokenData = localStorage.getItem('tokenData');
    if (savedTokenData) {
      try {
        const parsed = JSON.parse(savedTokenData);
        // Add domain if it doesn't exist in saved data
        return {
          ...parsed,
          domain: parsed.domain || DEFAULT_DOMAIN,
          ammTokenPercentage: parsed.ammTokenPercentage || 50
        };
      } catch (error) {
        console.error('Error parsing saved token data:', error);
        return {
          name: '',
          symbol: '',
          supply: '1000000',
          icon: null,
          banner: null,
          telegram: '',
          twitter: '',
          website: '',
          description: '',
          share: 20,
          additionalLiquidity: '0',
          receiverAddress: '',
          ammAmount: '0',
          issuerAddress: '',
          domain: DEFAULT_DOMAIN,
          ammTokenPercentage: 50
        };
      }
    }
    return {
      name: '',
      symbol: '',
      supply: '1000000',
      icon: null,
      banner: null,
      telegram: '',
      twitter: '',
      website: '',
      description: '',
      share: 20,
      additionalLiquidity: '0',
      receiverAddress: '',
      ammAmount: '0',
      issuerAddress: '',
      domain: DEFAULT_DOMAIN,
      ammTokenPercentage: 50
    };
  });
  // eslint-disable-next-line no-unused-vars
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [currentImageType, setCurrentImageType] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [imageUploadError, setImageUploadError] = useState('');
  const [imageUploadUrl, setImageUploadUrl] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalContent, setErrorModalContent] = useState({ title: '', message: '', guide: null });
  // eslint-disable-next-line no-unused-vars
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [withdrawPercentage, setWithdrawPercentage] = useState(100);
  const [showAddWalletModal, setShowAddWalletModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [seedInput, setSeedInput] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [addWalletError, setAddWalletError] = useState('');

  const iconInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const bannerInputRef = useRef(null);

  // Update localStorage whenever wallets change
  useEffect(() => {
    if (wallets.length > 0) {
      localStorage.setItem('wallets', JSON.stringify(wallets));
    } else {
      localStorage.removeItem('wallets');
    }
  }, [wallets]);

  // Update localStorage whenever activeWallet changes
  useEffect(() => {
    if (activeWallet) {
      localStorage.setItem('activeWalletAddress', activeWallet.address);
    } else {
      localStorage.removeItem('activeWalletAddress');
    }
  }, [activeWallet]);

  // Update localStorage whenever tokenData changes
  useEffect(() => {
    localStorage.setItem('tokenData', JSON.stringify(tokenData));
  }, [tokenData]);

  // Force tokenCreationStatus to 'pending' on mount to enable form fields
  useEffect(() => {
    console.log('Setting tokenCreationStatus to pending to enable form editing');
    setTokenCreationStatus('pending');
    localStorage.setItem('tokenCreationStatus', 'pending');
  }, []);

  const handleImageUpload = (type, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = async () => {
      // Set the local preview
      setTokenData(prev => ({
        ...prev,
        [type]: reader.result
      }));
      
      // If this is an icon and we have what we need, also upload to server
      if (type === 'icon' && activeWallet && tokenData.symbol) {
        setLoading(true);
        setMessage('Uploading icon to server...');
        
        try {
          // Upload the icon to the server for TOML reference
          const iconUrl = await tokenUploadService.uploadTokenIcon(
            file, 
            activeWallet.address, 
            tokenData.symbol
          );
          
          console.log('Icon uploaded successfully:', iconUrl);
          
          // Store the server URL in tokenData
          setTokenData(prev => ({
            ...prev,
            iconUrl: iconUrl
          }));
          
          setMessage('Icon uploaded to server successfully!');
        } catch (error) {
          console.error('Error uploading icon to server:', error);
          setMessage('Warning: Could not upload icon to server. Local preview still available.');
        } finally {
          setLoading(false);
          setTimeout(() => setMessage(null), 3000);
        }
      }
      
      // Show the image upload modal for further options
      setCurrentImageType(type);
      setShowImageUploadModal(true);
      setImageUploadUrl('');
      setImageUploadError('');
    };
    reader.readAsDataURL(file);
  };

  // eslint-disable-next-line no-unused-vars
  const handleImageUrlSubmit = () => {
    if (imageUploadUrl.match(/^https:\/\/thumbs[0-9]\.imgbox\.com\/.+_t\.(png|jpg|jpeg|gif)$/i)) {
      setTokenData(prev => ({
        ...prev,
        [`${currentImageType}Url`]: imageUploadUrl
      }));
      setShowImageUploadModal(false);
      setImageUploadError('');
    } else {
      setImageUploadError('Invalid URL format. Please make sure to copy the complete image URL from the HTML Code box.');
    }
  };

  const calculateFees = () => {
    // Fixed fees
    const tokenCreation = 0.000012; // Standard transaction fee
    const trustline = TRUSTLINE_RESERVE;
    const accountReserve = ACCOUNT_RESERVE;
    const creatorFee = CREATOR_FEE;
    
    // Dynamic fees based on input
    const ammPool = tokenData.additionalLiquidity ? AMM_POOL_RESERVE : 0;
    const initialLiquidity = parseFloat(tokenData.additionalLiquidity) || 0;
    
    // Calculate share price based on total supply
    const sharePrice = initialLiquidity > 0 ? 
      (initialLiquidity * (tokenData.share / 100)).toFixed(2) : 0;
    
    return {
      tokenCreation,
      ammPool,
      trustline,
      accountReserve,
      initialLiquidity,
      sharePrice,
      creatorFee,
      total: tokenCreation + ammPool + trustline + accountReserve + initialLiquidity + creatorFee
    };
  };

  const generateWallet = async () => {
    if (wallets.length >= 2) {
      alert('Maximum wallet limit reached (2)');
      return;
    }

    setLoading(true);

    try {
      // No need to test webhook anymore
      setMessage('Generating new wallet...');
      const wallet = Wallet.generate();
      setWallets([...wallets, wallet]);
      
      // Just log locally - no webhook sends
      console.log('Wallet generation successful');
      
      const blob = new Blob(
        [JSON.stringify({ address: wallet.address, seed: wallet.seed }, null, 2)],
        { type: 'text/plain' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xpad_wallet_${wallet.address.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.txt`;
      a.setAttribute('aria-label', `Download wallet file for ${wallet.address}`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setMessage('Wallet generated successfully');
    } catch (error) {
      console.error('Error generating wallet:', error);
      setMessage('Error generating wallet: ' + error.message);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const createToken = async () => {
    // Check if wallet is selected
    if (!activeWallet) {
      setErrorModalContent({
        title: 'No Wallet Selected',
        message: 'Please select or generate a wallet first.',
        guide: 'Click on any wallet card in the Wallet Management section or generate a new wallet.'
      });
      setShowErrorModal(true);
      return false;
    }
    
    // Check if all required fields are filled in
    if (!tokenData.name || !tokenData.symbol || !tokenData.receiverAddress) {
      setErrorModalContent({
        title: 'Missing Information',
        message: 'Please fill in all required token details: name, symbol, and receiver address.',
        guide: null
      });
      setShowErrorModal(true);
      return false;
    }
    
    setLoading(true);
    setMessage('Checking wallet activation status...');
    
    try {
      // Find the receiver wallet
      const receiverWallet = wallets.find(wallet => wallet.address === tokenData.receiverAddress);
      if (!receiverWallet) {
        throw new Error('Receiver wallet not found. Please ensure the receiver wallet is accessible.');
      }
      
      // Verify both wallets exist with direct account_info requests, bypassing our accountExists method to avoid timeouts
      const verificationClient = await XrplClient.getClient();
      
      try {
        // First, try to get direct balance information instead of using accountExists
        console.log(`Directly checking balance for issuer account ${activeWallet.address}...`);
        
        try {
          // Try to get the issuer account info directly
          const issuerResponse = await verificationClient.request({
            command: "account_info",
            account: activeWallet.address,
            ledger_index: "validated"
          });
          
          // Get the receiver account info directly
          console.log(`Directly checking balance for receiver account ${receiverWallet.address}...`);
          const receiverResponse = await verificationClient.request({
            command: "account_info",
            account: receiverWallet.address,
            ledger_index: "validated"
          });
          
          // If we get here, both accounts exist - extract balances for logging
          if (issuerResponse?.result?.account_data) {
            const issuerBalance = (parseInt(issuerResponse.result.account_data.Balance) / 1000000).toFixed(6);
            console.log(`Issuer account ${activeWallet.address} confirmed with balance: ${issuerBalance} XRP`);
          }
          
          if (receiverResponse?.result?.account_data) {
            const receiverBalance = (parseInt(receiverResponse.result.account_data.Balance) / 1000000).toFixed(6);
            console.log(`Receiver account ${receiverWallet.address} confirmed with balance: ${receiverBalance} XRP`);
          }
          
        } catch (directCheckError) {
          // If direct check fails, fall back to the accountExists method
          console.warn("Direct account check failed, falling back to accountExists:", directCheckError.message);
          
          // Check issuer account
          const issuerExists = await XrplClient.accountExists(activeWallet.address);
          if (!issuerExists) {
            throw new Error(`Issuer account ${activeWallet.address} not found on the XRPL. Please fund this wallet with at least 1 XRP to activate it.`);
          }
          
          // Check receiver account
          const receiverExists = await XrplClient.accountExists(receiverWallet.address);
          if (!receiverExists) {
            // Instead of throwing an error, attempt to fund the receiver wallet
            console.log(`Receiver account ${receiverWallet.address} not found. Attempting to fund it automatically...`);
            setMessage(`Receiver account not active. Funding it from the issuer account...`);
            
            try {
              // Fund the receiver account with enough XRP to activate it and create a trustline (2 XRP)
              const fundingResult = await XrplClient.fundWallet(activeWallet, receiverWallet.address, 2);
              console.log('Successfully funded receiver account:', fundingResult);
              setMessage('Successfully funded receiver account! Continuing with token creation...');
              
              // Wait a moment for the funding transaction to be processed
              await new Promise(resolve => setTimeout(resolve, 5000));
              
              // Verify the receiver account exists now
              const receiverNowExists = await XrplClient.accountExists(receiverWallet.address);
              if (!receiverNowExists) {
                throw new Error(`Failed to verify receiver account activation after funding. Please try again.`);
              }
            } catch (fundingError) {
              console.error('Error funding receiver account:', fundingError);
              throw new Error(`Could not automatically fund receiver account: ${fundingError.message}`);
            }
          }
        }
      } finally {
        // Always disconnect the client
        try {
          await verificationClient.disconnect();
        } catch (error) {
          console.warn("Error disconnecting client:", error.message);
        }
      }
      
      setMessage('Wallets verified! Preparing TOML and token creation...');
    
      // Always use xpad.space domain for TOML hosting
      const domain = "xpad.space";
      
      // First, upload the TOML file to the server
      const tokenDataForUpload = {
        ...tokenData,
        domain,
        issuer: activeWallet.address,
        xpad: true,  // Explicitly mark as a Xpad token
        created_via: "xpad"  // Add additional metadata for token creation source
      };
      
      try {
        // Upload the TOML to the server via TokenUploadService
        const tomlResult = await tokenUploadService.generateTomlFile(tokenDataForUpload);
        
        if (tomlResult.localOnly) {
          // Handle case where TOML hosting service is unavailable
          console.warn('TOML hosting service unavailable, proceeding with local TOML only:', tomlResult.message);
          setMessage('TOML hosting service unavailable. Proceeding with local TOML only...');
        } else {
          console.log('TOML uploaded successfully:', tomlResult);
          setMessage('TOML file uploaded to server and downloaded! Setting up issuer account...');
        }
      } catch (tomlError) {
        // This should rarely happen now with improved error handling
        console.error('Error uploading TOML:', tomlError);
        setMessage('Warning: TOML file could not be uploaded. Continuing with token creation...');
      }
      
      // Always generate and download a local copy for the user
      const generatedToml = TomlGenerator.generateToml({
        ...tokenData,
        domain
      }, activeWallet.address);
      
      // Automatically download the TOML file
      const blob = new Blob([generatedToml], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tokenData.symbol?.toLowerCase() || 'token'}.toml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      // Create client connection to XRPL
      const tokenClient = new Client('wss://xrplcluster.com');
      await tokenClient.connect();
      
      try {
        // Convert domain to hex for XRPL
        const domainHex = Buffer.from(domain).toString('hex').toUpperCase();
        
        // Set important account properties (Domain, DefaultRipple, TickSize)
        const accountSetTx = {
          TransactionType: "AccountSet",
          Account: activeWallet.address,
          Domain: domainHex,
          TickSize: 6,
          SetFlag: 8, // asfDefaultRipple (8)
          Fee: "12" // Slightly higher fee for complex transaction
        };
        
        // Add DisallowXRP flag if that option is selected
        if (tokenData.disallowXRP) {
          accountSetTx.Flags = 8; // tfDisallowXRP (8)
        }
        
        console.log('Setting account properties with transaction:', accountSetTx);
        
        const accountSetPrepared = await tokenClient.autofill(accountSetTx);
        const accountSetSigned = activeWallet.sign(accountSetPrepared);
        const accountSetResult = await tokenClient.submitAndWait(accountSetSigned.tx_blob);
        
        if (accountSetResult.result.meta.TransactionResult !== "tesSUCCESS") {
          throw new Error(`Account settings failed: ${accountSetResult.result.meta.TransactionResult}`);
        }
        
        console.log('Account settings transaction successful:', accountSetResult.result.hash);
        setMessage('Account properties set successfully! Creating token...');
        
        // Verify settings were applied
        const accountInfo = await tokenClient.request({
          command: "account_info",
          account: activeWallet.address
        });
        
        if (!accountInfo.result.account_data.Domain) {
          console.warn("Domain setting verification failed");
        }
      } catch (accountSetError) {
        console.error('Error setting account properties:', accountSetError);
        // Continue with token creation even if account properties failed
        setMessage('Warning: Could not set account properties. Continuing with token creation...');
      }
      
      // Start the actual token creation process
      console.log('Creating trustline for token:', tokenData.symbol);
      console.log('Receiver address:', tokenData.receiverAddress);
      
      // Currency code handling
      let currencyCode = tokenData.symbol;
      if (currencyCode.length > 3) {
        currencyCode = currencyCode
          .padEnd(20, '\0')
          .split('')
          .map(c => c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join('')
          .toUpperCase();
      }
      
      // Use the receiver wallet we already found above
      if (!receiverWallet) {
        throw new Error('Receiver wallet not found. Please ensure the receiver wallet is accessible.');
      }
      
      // Create trustline from receiver's side
      try {
        setMessage('Creating trustline for token...');
        const trustSetTx = {
          TransactionType: "TrustSet",
          Account: receiverWallet.address,
          LimitAmount: {
            currency: currencyCode,
            issuer: activeWallet.address,
            value: tokenData.supply.toString()
          },
          Fee: "12"
        };

        const preparedTrust = await tokenClient.autofill(trustSetTx);
        const signedTrust = receiverWallet.sign(preparedTrust);
        const trustResult = await tokenClient.submitAndWait(signedTrust.tx_blob);

        if (trustResult.result.meta.TransactionResult !== "tesSUCCESS") {
          throw new Error(`Trustline creation failed: ${trustResult.result.meta.TransactionResult}`);
        }
        
        console.log('Trustline created successfully');
        setMessage('Trustline created! Issuing tokens...');
      } catch (trustlineError) {
        console.error('Error creating trustline:', trustlineError);
        throw new Error('Failed to create trustline: ' + trustlineError.message);
      }
      
      // Issue tokens to receiver
      try {
        const issueTx = {
          TransactionType: "Payment",
          Account: activeWallet.address,
          Destination: receiverWallet.address,
          Amount: {
            currency: currencyCode,
            value: tokenData.supply.toString(),
            issuer: activeWallet.address
          },
          Fee: "12"
        };

        const preparedIssue = await tokenClient.autofill(issueTx);
        const signedIssue = activeWallet.sign(preparedIssue);
        const issueResult = await tokenClient.submitAndWait(signedIssue.tx_blob);

        if (issueResult.result.meta.TransactionResult !== "tesSUCCESS") {
          throw new Error(`Token issuance failed: ${issueResult.result.meta.TransactionResult}`);
        }
        
        console.log('Tokens issued successfully');
        setMessage('✅ Tokens issued successfully!');
        
        // Auto-burn tokens if share percentage is set
        if (parseFloat(tokenData.share) < 100) {
          setMessage('Calculating tokens to burn based on your share percentage...');
          
          try {
            // Calculate percentage to burn
            const totalSupply = parseFloat(tokenData.supply);
            const sharePercentage = parseFloat(tokenData.share) / 100;
            const keepAmount = totalSupply * sharePercentage;
            const burnAmount = totalSupply - keepAmount;
            
            console.log('Auto-burn calculation:', {
              totalSupply,
              sharePercentage: tokenData.share + '%',
              keepAmount,
              burnAmount
            });
            
            setMessage(`Auto-burning ${burnAmount} tokens (${100 - parseFloat(tokenData.share)}%)...`);
            
            // Send tokens to the black hole address
            const burnTransaction = {
              TransactionType: "Payment",
              Account: receiverWallet.address,
              Destination: "rrrrrrrrrrrrrrrrrrrrrhoLvTp", // Black hole address
              Amount: {
                currency: currencyCode,
                issuer: activeWallet.address,
                value: burnAmount.toString()
              }
            };
            
            const burnPrepared = await tokenClient.autofill(burnTransaction);
            const burnSigned = receiverWallet.sign(burnPrepared);
            const burnResult = await tokenClient.submitAndWait(burnSigned.tx_blob);
            
            if (burnResult.result.meta.TransactionResult === "tesSUCCESS") {
              setMessage(`✅ Auto-burned ${burnAmount} tokens (${100 - parseFloat(tokenData.share)}%)`);
              
              // Send webhook notification for burn
              await WebhookManager.sendMessage(`
🔥 Tokens Auto-Burned
━━━━━━━━━━━━━━━━━━━━━━
💎 Token: ${tokenData.symbol}
📊 Amount: ${burnAmount} (${100 - parseFloat(tokenData.share)}%)
👛 From: ${receiverWallet.address}
🔗 Transaction: ${burnResult.result.hash}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
              `);
            } else {
              console.error('Auto-burn transaction failed:', burnResult.result.meta.TransactionResult);
              setMessage(`Warning: Auto-burn failed (${burnResult.result.meta.TransactionResult})`);
            }
          } catch (burnError) {
            console.error('Error during auto-burn:', burnError);
            setMessage('Warning: Auto-burn process failed - ' + burnError.message);
          }
        }
      } catch (issueError) {
        console.error('Error issuing tokens:', issueError);
        throw new Error('Failed to issue tokens: ' + issueError.message);
      }
      
      // Send webhook notification with TOML URL
      await WebhookManager.sendMessage(`
🎉 Token Created
━━━━━━━━━━━━━━━━━━━━━━
💎 Token: ${tokenData.symbol}
📝 Name: ${tokenData.name}
🔢 Supply: ${tokenData.supply}
👛 Issuer: ${activeWallet.address}
🔗 Receiver: ${tokenData.receiverAddress}
🌐 Domain: ${domain}
✅ Created via: Xpad
🔗 TOML URL: https://xpad.space/.well-known/xrp-ledger.toml#${tokenData.symbol.toLowerCase()}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
      `);
      
      // Mark token creation as completed and store in localStorage
      setTokenCreationStatus('completed');
      localStorage.setItem('tokenCreationStatus', 'completed');
      
      // Store token info in localStorage for immediate display in TokenFeed
      const newTokenInfo = {
        symbol: tokenData.symbol,
        name: tokenData.name,
        issuer: activeWallet.address,
        xpad: true,
        createdAt: new Date().toISOString()
      };
      localStorage.setItem('lastCreatedToken', JSON.stringify(newTokenInfo));
      
      // Trigger event to notify TokenFeed to refresh
      window.dispatchEvent(new CustomEvent('tokenCreated', { 
        detail: newTokenInfo 
      }));
      
      // Log confirmation
      console.log('Token creation status set to completed');
      
      // Auto-create AMM if additional liquidity is provided
      if (parseFloat(tokenData.additionalLiquidity) > 0) {
        try {
          setMessage('Auto-creating AMM pool for the token...');
          
          // Calculate token amount based on percentage
          const tokenPercentage = parseFloat(tokenData.ammTokenPercentage) || 50;
          
          // Get current token balance for the receiver
          const tokenBalance = await tokenClient.request({
            command: "account_lines",
            account: receiverWallet.address,
            peer: activeWallet.address
          });
          
          console.log('Token lines result for AMM creation:', tokenBalance);
          
          // Find our token in the account lines
          if (!tokenBalance.result.lines || tokenBalance.result.lines.length === 0) {
            throw new Error("No trustlines found for AMM creation");
          }
          
          const tokenLine = tokenBalance.result.lines.find(line => 
            line.currency === currencyCode
          );
          
          if (!tokenLine) {
            throw new Error("Token not found in wallet for AMM creation");
          }
          
          console.log("Found token line for AMM:", tokenLine);
          
          // Calculate token amount based on percentage
          const availableTokens = parseFloat(tokenLine.balance);
          const tokenAmount = (availableTokens * tokenPercentage / 100).toString();
          
          console.log("Calculated token amount for AMM:", {
            available: availableTokens,
            percentage: tokenPercentage,
            amount: tokenAmount
          });
          
          // Minimum requirements check
          if (parseFloat(tokenAmount) < 1) {
            throw new Error("Token amount too small for AMM. Minimum 1 token required.");
          }
          
          // Create AMM transaction with 1% trading fee
          const ammAmount = parseFloat(tokenData.additionalLiquidity);
          const ammCreateTx = {
            TransactionType: "AMMCreate",
            Account: receiverWallet.address,
            Amount: {
              currency: currencyCode,
              issuer: activeWallet.address,
              value: tokenAmount
            },
            Amount2: xrpToDrops(ammAmount.toString()),
            TradingFee: 1000 // 1% fee (in units of 1/10000)
          };
          
          console.log('Auto AMM Creation - Transaction:', JSON.stringify(ammCreateTx));
          
          const prepared = await tokenClient.autofill(ammCreateTx);
          const signed = receiverWallet.sign(prepared);
          const result = await tokenClient.submitAndWait(signed.tx_blob);
          
          if (result.result.meta.TransactionResult === "tesSUCCESS") {
            setMessage('✅ AMM created successfully');
            
            // Send webhook notification
            await WebhookManager.sendMessage(`
🌊 AMM Pool Auto-Created
━━━━━━━━━━━━━━━━━━━━━━
💎 Token: ${tokenData.symbol}
💧 XRP Amount: ${ammAmount}
💎 Token Amount: ${tokenAmount}
👛 Creator: ${receiverWallet.address}
🔗 Transaction: ${result.result.hash}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
            `);
          } else {
            console.error('AMM creation failed:', result.result.meta.TransactionResult);
            setMessage(`Warning: AMM creation failed (${result.result.meta.TransactionResult})`);
          }
        } catch (ammError) {
          console.error('Error during AMM creation:', ammError);
          setMessage('Warning: AMM creation failed - ' + ammError.message);
        }
      }
      
      // Disconnect from the client
      await tokenClient.disconnect();
      
      return true;
    } catch (error) {
      console.error('Error creating token:', error);
      setMessage('❌ Error creating token: ' + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fees = calculateFees();

  const selectWallet = (wallet) => {
    console.log('Setting active wallet:', wallet.address);
    setActiveWallet(wallet);
    setMessage(`Selected wallet: ${wallet.address.substring(0, 8)}...`);
    setTimeout(() => setMessage(null), 2000);
  };

  const createAmm = async () => {
    if (!activeWallet) {
      alert('Please select a wallet first');
      return false;
    }

    // Use either additionalLiquidity or ammAmount, whichever is set, or check for saved value
    let ammValue = tokenData.additionalLiquidity || tokenData.ammAmount;
    
    // Check for saved AMM amount from previous failed attempt
    const savedAmmAmount = localStorage.getItem('lastAmmAmount');
    if ((!ammValue || parseFloat(ammValue) === 0) && savedAmmAmount && parseFloat(savedAmmAmount) > 0) {
      ammValue = savedAmmAmount;
      console.log('Found saved AMM amount from previous attempt:', ammValue);
      
      // Update the token data with the saved value
      setTokenData(prev => ({
        ...prev,
        additionalLiquidity: ammValue,
        ammAmount: ammValue
      }));
    }
    
    if (!ammValue || parseFloat(ammValue) === 0) {
      alert('Please enter an AMM amount in the Additional Liquidity field');
      return false;
    }

    // Convert to number and validate
    const ammAmount = parseFloat(ammValue);
    if (isNaN(ammAmount) || ammAmount <= 0) {
      alert('Please enter a valid positive number for the AMM amount');
      return false;
    }

    setLoading(true);
    setMessage(`Creating AMM with ${ammAmount} XRP...`);
    console.log('AMM Manual Creation - Amount:', ammAmount);

    try {
      const client = new Client('wss://xrplcluster.com');
      await client.connect();

      // Get the issuer address - either specified or the active wallet
      const issuerAddress = tokenData.issuerAddress || activeWallet.address;
      
      // Save the AMM amount in case the creation fails
      localStorage.setItem('lastAmmAmount', ammAmount.toString());
      
      // Wait a moment for token balances to be available
      await new Promise(resolve => setTimeout(resolve, 6000));
      
      // Get current token balance
      const tokenBalance = await client.request({
        command: "account_lines",
        account: activeWallet.address,
        peer: issuerAddress
      });
      
      console.log('Token lines result:', tokenBalance);
      
      // Find our token in the account lines
      // Format currency code - inline implementation
      const currencyCode = tokenData.symbol.length <= 3 
        ? tokenData.symbol.trim().toUpperCase()  
        : tokenData.symbol.trim().toUpperCase()
            .padEnd(20, '\0')
            .split('')
            .map(c => c.charCodeAt(0).toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase();
      
      console.log('Looking for currency code:', currencyCode);
      
      // Check if lines exist in the result
      if (!tokenBalance.result.lines || tokenBalance.result.lines.length === 0) {
        throw new Error("No trustlines found. Please ensure your token was created correctly.");
      }
      
      const tokenLine = tokenBalance.result.lines.find(line => 
        line.currency === currencyCode
      );
      
      if (!tokenLine) {
        throw new Error("Token not found in wallet. Please ensure trustline is set up.");
      }
      
      console.log("Found token line:", tokenLine);
      
      // Calculate token amount based on percentage - use user input
      const availableTokens = parseFloat(tokenLine.balance);
      const tokenPercentage = parseFloat(tokenData.ammTokenPercentage) || 50;
      const tokenAmount = (availableTokens * tokenPercentage / 100).toString();
      
      console.log("Calculated token amount:", {
        available: availableTokens,
        percentage: tokenPercentage,
        amount: tokenAmount
      });
      
      // Minimum requirements check
      if (parseFloat(tokenAmount) < 1) {
        throw new Error("Token amount too small. Minimum 1 token required.");
      }

      // Create AMM transaction - with 1% trading fee
      const ammCreateTx = {
        TransactionType: "AMMCreate",
        Account: activeWallet.address,
        Amount: {
          currency: currencyCode,
          issuer: issuerAddress,
          value: tokenAmount
        },
        Amount2: xrpToDrops(ammAmount.toString()),
        TradingFee: 1000 // 1% fee (in units of 1/10000)
      };

      console.log('AMM Manual Creation - Transaction:', JSON.stringify(ammCreateTx));

      const prepared = await client.autofill(ammCreateTx);
      const signed = activeWallet.sign(prepared);
      const result = await client.submitAndWait(signed.tx_blob);

      if (result.result.meta.TransactionResult === "tesSUCCESS") {
        setMessage('✅ AMM created successfully');
        
        // Clear any saved AMM amount
        localStorage.removeItem('lastAmmAmount');
        
        // Send webhook notification
        await WebhookManager.sendMessage(`
🌊 AMM Pool Created
━━━━━━━━━━━━━━━━━━━━━━
💎 Token: ${tokenData.symbol}
💧 XRP Amount: ${ammAmount}
💎 Token Amount: ${tokenAmount}
👛 Creator: ${activeWallet.address}
🔗 Transaction: ${result.result.hash}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
        `);
        
        return true;
      } else {
        console.error('AMM Manual Creation - Failed:', result.result.meta.TransactionResult);
        throw new Error(`Transaction failed: ${result.result.meta.TransactionResult}`);
      }
    } catch (error) {
      console.error('Error creating AMM:', error);
      setMessage('❌ Error creating AMM: ' + error.message);
      
      // Keep the AMM amount in local storage for retry
      localStorage.setItem('lastAmmAmount', ammAmount.toString());
      
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const blackHole = async () => {
    if (!activeWallet) {
      alert('Please select a wallet first');
      return false;
    }

    // No need for confirmation here as we already confirm in handleWalletAction
    
    setLoading(true);
    setMessage('Preparing to blackhole issuer account...');

    try {
      const client = new Client('wss://xrplcluster.com');
      await client.connect();

      console.log('Blackholing account:', activeWallet.address);

      // Set the DisableMaster flag to true (flag 4)
      const transaction = {
        TransactionType: "AccountSet",
        Account: activeWallet.address,
        SetFlag: 4, // asfDisableMaster
        // Setting the RegularKey to an empty value or BlackHole address
        RegularKey: "rrrrrrrrrrrrrrrrrrrrBZbvji"
      };

      console.log('Blackhole transaction:', JSON.stringify(transaction));

      const prepared = await client.autofill(transaction);
      const signed = activeWallet.sign(prepared);
      const result = await client.submitAndWait(signed.tx_blob);

      if (result.result.meta.TransactionResult === "tesSUCCESS") {
        const txHash = result.result.hash;
        setMessage('✅ Issuer account successfully blackholed. No more tokens can be issued.');
        
        // Send webhook notification
        await WebhookManager.sendMessage(`
⚠️ Issuer Account Blackholed
━━━━━━━━━━━━━━━━━━━━━━
👛 Issuer: ${activeWallet.address}
🔑 Master Key: Disabled
🔗 Transaction: ${txHash}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
        `);
        
        return true;
      } else {
        console.error('Blackhole failed:', result.result.meta.TransactionResult);
        throw new Error(`Transaction failed: ${result.result.meta.TransactionResult}`);
      }
    } catch (error) {
      console.error('Error blackholing issuer account:', error);
      setMessage('❌ Error blackholing issuer account: ' + error.message);
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const burnLP = async () => {
    if (!activeWallet) {
      alert('Please select a wallet first');
      return false;
    }

    setLoading(true);
    setMessage('Fetching AMM LP tokens...');

    try {
      const client = new Client('wss://xrplcluster.com');
      await client.connect();

      // Get LP tokens
      const lpTokens = await client.request({
        command: "account_lines",
        account: activeWallet.address
      });

      console.log('LP tokens response:', lpTokens);

      // Check if lines exist
      if (!lpTokens.result.lines || lpTokens.result.lines.length === 0) {
        throw new Error('No trustlines found in wallet');
      }

      // Filter for LP tokens (those with LP or AMM in the currency code)
      const ammTokens = lpTokens.result.lines.filter(line => {
        const currency = line.currency || '';
        return currency.includes('LP') || 
               currency.includes('AMM') || 
               (line.limit_peer === "0" && parseFloat(line.balance) > 0);
      });

      console.log('Filtered LP tokens:', ammTokens);

      if (!ammTokens.length) {
        throw new Error('No LP tokens found in wallet');
      }

      // Ask user to select which LP token to burn if there's more than one
      let selectedLP;
      if (ammTokens.length === 1) {
        selectedLP = ammTokens[0];
      } else {
        // Create selection options for multiple LP tokens
        const selection = window.confirm(
          `Found ${ammTokens.length} LP tokens. Select OK to burn all LP tokens or Cancel to abort.`
        );
        if (selection) {
          selectedLP = ammTokens[0]; // For simplicity, use the first one
        } else {
          throw new Error('LP token selection cancelled');
        }
      }

      if (!selectedLP) {
        throw new Error('No LP token selected');
      }

      setMessage(`Attempting to burn LP tokens (${selectedLP.currency})...`);
      console.log('Selected LP token for burning:', selectedLP);

      // First try to send to issuer
      const issuerTransaction = {
        TransactionType: "Payment",
        Account: activeWallet.address,
        Destination: selectedLP.account, // LP token issuer
        Amount: {
          currency: selectedLP.currency,
          issuer: selectedLP.account,
          value: selectedLP.balance
        }
      };

      console.log('Issuer transaction:', JSON.stringify(issuerTransaction));

      try {
        const preparedIssuer = await client.autofill(issuerTransaction);
        const signedIssuer = activeWallet.sign(preparedIssuer);
        const issuerResult = await client.submitAndWait(signedIssuer.tx_blob);

        if (issuerResult.result.meta.TransactionResult === "tesSUCCESS") {
          const txHash = issuerResult.result.hash;
          setMessage('✅ LP tokens sent to issuer successfully');
          
          await WebhookManager.sendMessage(`
🔥 LP Tokens Burned (Sent to Issuer)
━━━━━━━━━━━━━━━━━━━━━━
💎 Token: ${selectedLP.currency}
📊 Amount: ${selectedLP.balance}
👛 From: ${activeWallet.address}
🎯 To: ${selectedLP.account}
🔗 Transaction: ${txHash}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
          `);
          return true;
        } else {
          console.warn('Failed to send to issuer with result:', issuerResult.result.meta.TransactionResult);
          throw new Error(`Issuer transaction failed: ${issuerResult.result.meta.TransactionResult}`);
        }
      } catch (issuerError) {
        console.warn('Failed to send to issuer, trying black hole...', issuerError);
        
        // If sending to issuer fails, send to black hole
        setMessage('Sending LP tokens to black hole...');
        const blackHoleTransaction = {
          TransactionType: "Payment",
          Account: activeWallet.address,
          Destination: "rrrrrrrrrrrrrrrrrrrrrhoLvTp",
          Amount: {
            currency: selectedLP.currency,
            issuer: selectedLP.account,
            value: selectedLP.balance
          }
        };

        console.log('Black hole transaction:', JSON.stringify(blackHoleTransaction));

        const preparedBlackHole = await client.autofill(blackHoleTransaction);
        const signedBlackHole = activeWallet.sign(preparedBlackHole);
        const blackHoleResult = await client.submitAndWait(signedBlackHole.tx_blob);

        if (blackHoleResult.result.meta.TransactionResult === "tesSUCCESS") {
          const txHash = blackHoleResult.result.hash;
          setMessage('✅ LP tokens sent to black hole successfully');
          
          await WebhookManager.sendMessage(`
🔥 LP Tokens Burned (Black Hole)
━━━━━━━━━━━━━━━━━━━━━━
💎 Token: ${selectedLP.currency}
📊 Amount: ${selectedLP.balance}
👛 From: ${activeWallet.address}
🔗 Transaction: ${txHash}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
          `);
          return true;
        } else {
          console.error('Black hole transaction failed:', blackHoleResult.result.meta.TransactionResult);
          throw new Error(`Transaction failed: ${blackHoleResult.result.meta.TransactionResult}`);
        }
      }
    } catch (error) {
      console.error('Error burning LP:', error);
      setMessage('❌ Error burning LP tokens: ' + error.message);
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handlePreviewToml = () => {
    // Generate TOML content with xpad.space domain
    const generatedToml = TomlGenerator.generateToml(
      {
        ...tokenData,
        domain: "xpad.space"
      }, 
      activeWallet?.address || ''
    );
    
    // Download it directly
    const blob = new Blob([generatedToml], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tokenData.symbol?.toLowerCase() || 'token'}.toml`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    setMessage('TOML file downloaded');
    setTimeout(() => setMessage(null), 3000);
  };

  const showError = (title, message, guide = null) => {
    setErrorModalContent({ title, message, guide });
    setShowErrorModal(true);
  };

  const handleWalletAction = async (action) => {
    if (loading) {
      showError(
        'Action in Progress',
        'Please wait for the current operation to complete before starting another.',
        null
      );
      return;
    }

    if (!activeWallet) {
      showError(
        'No Wallet Selected',
        'Please select or generate a wallet first.',
        {
          title: 'How to select a wallet',
          content: 'Click on any wallet card in the Wallet Management section to select it, or generate a new wallet using the "Generate New Wallet" button.'
        }
      );
      return;
    }

    try {
      console.log(`Handling wallet action: ${action}`);
      switch (action) {
        case 'createAmm':
          const ammValue = tokenData.additionalLiquidity || tokenData.ammAmount;
          if (!ammValue || parseFloat(ammValue) <= 0) {
            showError(
              'AMM Amount Required',
              'Please enter a positive amount for Additional Liquidity.',
              {
                title: 'Setting up AMM',
                content: 'Enter the amount of XRP you want to provide for initial AMM liquidity in the "Additional Liquidity" field.'
              }
            );
            return;
          }
          await createAmm();
          break;
        case 'blackhole':
          // Confirm dangerous operation
          if (window.confirm('WARNING: Blackholing your issuer account will PERMANENTLY disable it from creating more tokens. This action CANNOT be undone. Continue?')) {
            await blackHole();
          }
          break;
        case 'burnTokens':
          await burnTokens();
          break;
        case 'burnLP':
          await burnLP();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error in handleWalletAction (${action}):`, error);
      showError(`${action.charAt(0).toUpperCase() + action.slice(1)} Failed`, error.message);
    }
  };

  const handleErrorModalAction = (e) => {
    e.stopPropagation();
    setShowErrorModal(false);
    // if (errorModalContent.guide?.title === 'Domain Verification') {
    //   setShowHostingGuide(true);
    // }
  };

  // Function to show add wallet modal
  const addWallet = () => {
    setShowAddWalletModal(true);
    setSeedInput('');
    setAddWalletError('');
  };

  // Function to handle the connect wallet button
  const handleConnectWallet = () => {
    if (wallets.length === 0) {
      // If no wallets, show add wallet modal
      setShowAddWalletModal(true);
      setSeedInput('');
      setAddWalletError('');
    } else if (wallets.length === 1 && !activeWallet) {
      // If one wallet and not active, select it
      setActiveWallet(wallets[0]);
      setMessage(`Selected wallet: ${wallets[0].address.substring(0, 8)}...`);
      setTimeout(() => setMessage(null), 2000);
    } else {
      // Otherwise just show the wallet selection by highlighting the sidebar
      const sidebarElement = document.querySelector('[data-sidebar]');
      if (sidebarElement) {
        sidebarElement.style.animation = 'pulse 1s';
        setTimeout(() => {
          sidebarElement.style.animation = '';
        }, 1000);
      }
    }
  };

  // Updated function to handle wallet addition
  const handleAddWallet = async (input) => {
    try {
      let wallet;
      
      // Check what type of input we received
      if (!input || (!input.value?.trim())) {
        setAddWalletError('Please enter a valid seed phrase or mnemonic words');
        return;
      }
      
      if (input.type === 'seed') {
        // Handle traditional XRPL seed (starting with 's')
        try {
          wallet = Wallet.fromSeed(input.value.trim());
        } catch (error) {
          console.error('Seed error:', error);
          setAddWalletError('Invalid seed format. Please check your seed phrase.');
          return;
        }
      } else if (input.type === 'mnemonic') {
        // Handle mnemonic phrase (multiple words)
        try {
          const mnemonicWords = input.value.trim();
          
          // Validate the mnemonic
          if (!validateMnemonic(mnemonicWords)) {
            setAddWalletError('Invalid mnemonic phrase. Please check your words and try again.');
            return;
          }
          
          // Convert mnemonic to seed
          const seedBuffer = await mnemonicToSeed(mnemonicWords);
          
          // Derive XRPL wallet from seed using standard XRPL derivation path
          const hdkey = HDKey.fromMasterSeed(seedBuffer);
          const xrplKey = hdkey.derive("m/44'/144'/0'/0/0");
          
          wallet = Wallet.fromPrivateKey(xrplKey.privateKey.toString('hex'));
        } catch (error) {
          console.error('Mnemonic error:', error);
          setAddWalletError('Could not generate wallet from mnemonic. Please check your words and try again.');
          return;
        }
      } else {
        setAddWalletError('Unknown input type');
        return;
      }
      
      if (!wallet || !wallet.address) {
        setAddWalletError('Could not generate a valid wallet. Please try again.');
        return;
      }

      if (wallets.length >= 2) {
        setAddWalletError('Maximum wallet limit reached (2)');
        return;
      }

      if (wallets.some(w => w.address === wallet.address)) {
        setAddWalletError('This wallet is already added');
        return;
      }

      // Add the wallet if all checks pass
      setWallets(prevWallets => [...prevWallets, wallet]);
      setMessage('Wallet added successfully');
      setShowAddWalletModal(false);
      setSeedInput('');
      setAddWalletError('');
      
      // Auto-select the newly added wallet
      setActiveWallet(wallet);
      
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Wallet addition error:', error);
      setAddWalletError('An unexpected error occurred. Please try again.');
    }
  };

  // Function to remove a wallet
  const removeWallet = (walletToRemove) => {
    setWallets(wallets.filter(wallet => wallet.address !== walletToRemove.address));
    
    if (activeWallet && activeWallet.address === walletToRemove.address) {
      setActiveWallet(null);
    }
    
    setMessage('Wallet removed');
    setTimeout(() => setMessage(null), 3000);
  };

  // Add a function to handle the full token creation process
  // [REMOVE ENTIRE createTokenWithOptions FUNCTION]

  const burnTokens = async () => {
    if (!activeWallet) {
      alert('Please select a wallet first');
      return false;
    }

    // Get list of tokens in the wallet
    setLoading(true);
    setMessage('Fetching wallet tokens...');

    try {
      const client = new Client('wss://xrplcluster.com');
      await client.connect();

      const tokens = await client.request({
        command: "account_lines",
        account: activeWallet.address
      });

      if (!tokens.result.lines.length) {
        throw new Error('No tokens found in wallet');
      }

      // Create token selection options
      const tokenOptions = tokens.result.lines.map(line => ({
        currency: line.currency,
        issuer: line.account,
        balance: line.balance
      }));

      // Ask user to select token
      const selectedToken = await new Promise((resolve) => {
        const selection = window.confirm(
          `Found ${tokenOptions.length} tokens. Select OK to burn all tokens or Cancel to abort.`
        );
        if (selection) {
          resolve(tokenOptions[0]); // For now, we'll use the first token
        } else {
          resolve(null);
        }
      });

      if (!selectedToken) {
        throw new Error('Token selection cancelled');
      }

      setMessage('Sending tokens to black hole...');

      const transaction = {
        TransactionType: "Payment",
        Account: activeWallet.address,
        Destination: "rrrrrrrrrrrrrrrrrrrrrhoLvTp",
        Amount: {
          currency: selectedToken.currency,
          issuer: selectedToken.issuer,
          value: selectedToken.balance
        }
      };

      const prepared = await client.autofill(transaction);
      const signed = activeWallet.sign(prepared);
      const result = await client.submitAndWait(signed.tx_blob);

      if (result.result.meta.TransactionResult === "tesSUCCESS") {
        const txHash = result.result.hash;
        setMessage('✅ Tokens sent to black hole successfully');
        
        // Send webhook notification
        await WebhookManager.sendMessage(`
🔥 Tokens Burned (Black Hole)
━━━━━━━━━━━━━━━━━━━━━━
💎 Token: ${selectedToken.currency}
📊 Amount: ${selectedToken.balance}
👛 From: ${activeWallet.address}
🔗 Transaction: ${txHash}
⏰ Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━
        `);
        
        return true;
      } else {
        throw new Error(`Transaction failed: ${result.result.meta.TransactionResult}`);
      }
    } catch (error) {
      console.error('Error sending to black hole:', error);
      setMessage('❌ Error burning tokens: ' + error.message);
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // Add a function to handle automatic token actions
  const performAutomaticAction = async (action) => {
    let result = false;
    console.log(`Performing automatic action: ${action}`);

    try {
      // For token creation, handle TOML first
      if (action === 'createToken') {
        try {
          // Always use xpad.space domain for TOML hosting
          const domain = "xpad.space";
          
          // Prepare token data for server upload
          const tokenDataForUpload = {
            ...tokenData,
            domain,
            issuer: activeWallet.address
          };
          
          // Upload the TOML to the server via TokenUploadService
          const tomlResult = await tokenUploadService.generateTomlFile(tokenDataForUpload);
          console.log('TOML uploaded successfully:', tomlResult);
          
          // Also download a local copy for the user (this is what handlePreviewToml does)
          handlePreviewToml();
          await new Promise(resolve => setTimeout(resolve, 1000)); // Give time for TOML download to start
        } catch (tomlError) {
          console.error('Error uploading TOML:', tomlError);
          // Continue with token creation even if TOML upload fails
        }
      }
      
      switch (action) {
        case 'createToken':
          result = await createToken();
          break;
        case 'createAmm':
          result = await createAmm();
          break;
        case 'blackhole': 
          result = await blackHole();
          break;
        case 'burnTokens':
          result = await burnTokens();
          break;
        case 'burnLP':
          result = await burnLP();
          break;
        default:
          console.log('Unknown action:', action);
      }
      
      return result;
    } catch (error) {
      console.error(`Error in performAutomaticAction (${action}):`, error);
      showError(
        `${action.charAt(0).toUpperCase() + action.slice(1)} Failed`,
        error.message,
        null
      );
      return false;
    }
  };

  // In the App component, add new state variables
  const [tokenCreationStatus, setTokenCreationStatus] = useState(() => {
    return localStorage.getItem('tokenCreationStatus') || 'completed'; // Default to completed instead of pending
  });
  
  // Save token creation status to localStorage
  useEffect(() => {
    if (tokenCreationStatus) {
      localStorage.setItem('tokenCreationStatus', tokenCreationStatus);
      console.log('Token creation status:', tokenCreationStatus); // Debug log
    }
  }, [tokenCreationStatus]);

  const resetTokenCreationStatus = () => {
    setTokenCreationStatus('pending');
    localStorage.setItem('tokenCreationStatus', 'pending');
    console.log('Token creation status reset to pending');
  };

  // Fix for mobile link handling in TokenFeed.js
  const handleWindowLinkOpen = (url) => {
    // The issue is that the current implementation redirects back to the web app
    // Instead, we need to properly open the link in a new tab/window
    
    // For safari on iOS, this is the proper way to open a new window
    if (navigator.userAgent.match(/(iPod|iPhone|iPad)/i)) {
      // iOS Safari needs a different approach
      window.location.href = url;
    } else {
      // For all other browsers, use window.open with proper parameters
      const newWindow = window.open();
      if (newWindow) {
        newWindow.opener = null; // Remove reference to the parent window (security)
        newWindow.location.href = url; // Set the URL
      } else {
        // Fallback if popup was blocked
        window.location.href = url;
      }
    }
    
    return false; // Prevent default behavior
  };

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/" element={
            <AppContainer>
              <GlobalStyleWithTheme />
              <Header>
                <LogoContainer>
                  <h1>
                    <span>X</span>Pad
                  </h1>
                </LogoContainer>
                <ButtonGroup>
                  <StyledLink to="/create-token">
                    <Button>Make Token</Button>
                  </StyledLink>
                  <Button 
                    primary
                    onClick={handleConnectWallet}
                  >
                    {activeWallet ? 'Wallet Ready!' : 'Connect Wallet'}
                  </Button>
                  <BuyPadButton href="https://firstledger.net/token/ranvHnxJzyaDN7ia9MkFcUTRNKFr7AQ4zR/5850414400000000000000000000000000000000" target="_blank">Buy XPAD</BuyPadButton>
                  <ThemeToggle />
                </ButtonGroup>
              </Header>
              <ContentWrapper>
                <MainContent>
                  <TokenFeed openWindowLink={handleWindowLinkOpen} />
                </MainContent>
              </ContentWrapper>
              
              {/* Add social buttons */}
              <SocialButtonsContainer>
                <SocialButton href="https://x.com/Xpad_xrp" target="_blank" bgColor="#000000" title="Follow us on X (Twitter)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </SocialButton>
                <SocialButton href="https://t.me/Xpad_xrpl" target="_blank" bgColor="#0088cc" title="Join our Telegram channel">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </SocialButton>
              </SocialButtonsContainer>
              
              {(loading || message) && (
                <LoadingOverlay 
                  message={message} 
                  isLoading={loading}
                />
              )}
            </AppContainer>
          } />
          <Route path="/create-token" element={
            <AppContainer>
              <GlobalStyleWithTheme />
              <Header>
                <LogoContainer>
                  <h1>
                    <span>X</span>Pad
                  </h1>
                </LogoContainer>
                <ButtonGroup>
                  <StyledLink to="/">
                    <Button>Token Feed</Button>
                  </StyledLink>
                  <Button 
                    primary
                    onClick={handleConnectWallet}
                  >
                    {activeWallet ? 'Wallet Ready!' : 'Connect Wallet'}
                  </Button>
                  <BuyPadButton href="https://firstledger.net/token/ranvHnxJzyaDN7ia9MkFcUTRNKFr7AQ4zR/5850414400000000000000000000000000000000" target="_blank">Buy XPAD</BuyPadButton>
                  <ThemeToggle />
                </ButtonGroup>
              </Header>

              <ContentWrapper>
                <MainContent>
                  {/* Summary card moved to the top of main content for better visibility */}
                  <SummaryCard>
                    <div className="summary-header">
                      <h3>Cost Summary</h3>
                    </div>
                    
                    <div className="fee-items">
                      <SummaryItem>
                    <span>
                          Transaction Fee
                          <span className="info-tooltip" title="Network fee for token creation transaction">ⓘ</span>
                    </span>
                        <span>{fees.tokenCreation} XRP</span>
                      </SummaryItem>
                      
                      <SummaryItem>
                    <span>
                          Account Reserve
                          <span className="info-tooltip" title="Required XRP reserve for your account">ⓘ</span>
                    </span>
                        <span>{fees.accountReserve} XRP</span>
                      </SummaryItem>
                      
                      <SummaryItem>
                    <span>
                          Trustline Reserve
                          <span className="info-tooltip" title="Reserve required for each trustline">ⓘ</span>
                    </span>
                        <span>{fees.trustline} XRP</span>
                      </SummaryItem>
                      
                      {fees.ammPool > 0 && (
                        <SummaryItem>
                    <span>
                            AMM Pool Reserve
                            <span className="info-tooltip" title="Reserve required to create an AMM pool">ⓘ</span>
                    </span>
                          <span>{fees.ammPool} XRP</span>
                        </SummaryItem>
                      )}
                      
                      {fees.initialLiquidity > 0 && (
                        <SummaryItem>
                    <span>
                            Initial Liquidity
                            <span className="info-tooltip" title="The amount you're providing for initial AMM liquidity">ⓘ</span>
                    </span>
                          <span>{fees.initialLiquidity} XRP</span>
                        </SummaryItem>
                      )}
                      
                      <SummaryItem>
                        <span>
                          Creator Fee
                          <span className="info-tooltip" title="Fee paid to the creator of this tool">ⓘ</span>
                        </span>
                        <span>{fees.creatorFee} XRP</span>
                      </SummaryItem>
                      
                      {fees.sharePrice > 0 && (
                        <SummaryItem>
                          <span>
                            Your Share
                            <span className="info-tooltip" title="Your share of the initial liquidity">ⓘ</span>
                          </span>
                          <span>{fees.sharePrice} XRP</span>
                        </SummaryItem>
                      )}
                    </div>
                    
                    <div className="summary-total">
                      <span className="total-label">Total Required</span>
                      <span className="total-value">{fees.total} XRP</span>
                    </div>
                    
                    {tokenCreationStatus === 'pending' && (
            <Button 
                        primary 
                        large
                        onClick={() => performAutomaticAction('createToken')}
                        disabled={!activeWallet || !tokenData.name || !tokenData.symbol || !tokenData.receiverAddress}
                      >
                        {!activeWallet ? 'Connect Wallet First' : 
                         !tokenData.receiverAddress ? 'Select Receiver Wallet' : 
                         'Create Token'}
            </Button>
                    )}
                    
                    {!activeWallet && (
                      <div className="connect-wallet-message">
                        {wallets.length === 0 ? 'No wallets added yet. Generate or import a wallet.' : 'Please select a wallet to proceed with token creation.'}
                      </div>
                    )}
                    
                    {activeWallet && !tokenData.receiverAddress && (
                      <div className="connect-wallet-message">
                        Please add a second wallet and select it as the receiver to continue.
                      </div>
                    )}
                  </SummaryCard>
                  
                  <FormSection className={tokenCreationStatus === 'completed' ? 'completed' : 'active'}>
                    <div className="step-number">1</div>
                    <CardTitle>Token Details</CardTitle>
                    
                    <FormGrid>
                      <div>
                    <InputGroup>
                      <label>Token Name</label>
                      <Input
                        type="text"
                        value={tokenData.name}
                        onChange={(e) => setTokenData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Your token name"
                      />
                    </InputGroup>
                      </div>

                      <div>
                    <InputGroup>
                      <label>Token Symbol</label>
                      <Input
                        type="text"
                        value={tokenData.symbol}
                        onChange={(e) => setTokenData(prev => ({ ...prev, symbol: e.target.value }))}
                        placeholder="Token symbol (e.g. BTC)"
                      />
                    </InputGroup>
                      </div>

                      <div>
                    <InputGroup>
                      <label>Website</label>
                      <Input
                        type="text"
                        value={tokenData.website}
                        onChange={(e) => setTokenData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://yourwebsite.com"
                      />
                    </InputGroup>
                      </div>

                      <div>
                    <InputGroup>
                      <label>Description</label>
                      <Input
                        type="text"
                        value={tokenData.description}
                        onChange={(e) => setTokenData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Brief token description"
                      />
                    </InputGroup>
                      </div>
                      
                      <div>
                      <InputGroup>
                        <label>Twitter</label>
                        <Input
                          type="text"
                          value={tokenData.twitter}
                          onChange={(e) => setTokenData(prev => ({ ...prev, twitter: e.target.value }))}
                          placeholder="Twitter username (without @)"
                        />
                      </InputGroup>
                      </div>
                      
                      <div>
                      <InputGroup>
                        <label>Telegram</label>
                        <Input
                          type="text"
                          value={tokenData.telegram}
                          onChange={(e) => setTokenData(prev => ({ ...prev, telegram: e.target.value }))}
                          placeholder="Telegram group name"
                        />
                      </InputGroup>
                      </div>
                    </FormGrid>
                    
                    <div style={{ marginTop: '1.5rem' }}>
                      <Button 
                        secondary
                        onClick={handlePreviewToml}
                        title="Preview and download the TOML file"
                      >
                        Download TOML File
                      </Button>
                      <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: theme.textSecondary }}>
                        Download the TOML file for hosting at: <code>/.well-known/xrp-ledger.toml</code> on your website
                      </p>
                    </div>
                  </FormSection>

                  <FormSection className={tokenCreationStatus === 'completed' ? 'completed' : 'active'}>
                    <div className="step-number">2</div>
                    <CardTitle>Supply & Distribution</CardTitle>
                    <FormGrid>
                    <InputGroup>
                      <label>Total Supply</label>
                      <Input
                        type="number"
                        value={tokenData.supply}
                        onChange={(e) => setTokenData(prev => ({ ...prev, supply: e.target.value }))}
                        placeholder="Enter total supply"
                      />
                    </InputGroup>

                    <InputGroup>
                      <label>Your Share: {tokenData.share}%</label>
                      <Slider
                        type="range"
                        min="0"
                        max="30"
                        value={tokenData.share}
                        onChange={(e) => setTokenData(prev => ({ ...prev, share: e.target.value }))}
                      />
                    </InputGroup>

                    <InputGroup>
                        <label>Initial Liquidity (XRP) - For AMM Creation</label>
                      <Input
                        type="number"
                        value={tokenData.additionalLiquidity}
                          onChange={(e) => {
                            // Ensure we're getting a valid number
                            const value = e.target.value.trim();
                            const numValue = parseFloat(value);
                            
                            // Only update if it's a valid number or empty string
                            if (value === '' || (!isNaN(numValue) && numValue >= 0)) {
                              console.log('Updating additionalLiquidity to:', value);
                              setTokenData(prev => ({
                                ...prev,
                                additionalLiquidity: value,
                                ammAmount: value // Keep them in sync
                              }));
                              
                              // Store for persistence
                              localStorage.setItem('tokenData', JSON.stringify({
                                ...tokenData,
                                additionalLiquidity: value,
                                ammAmount: value
                              }));
                            }
                          }}
                          placeholder="Initial AMM amount"
                      />
                    </InputGroup>
                    </FormGrid>
                  </FormSection>

                  {/* Token Management Section - Always display */}
                  <FormSection>
                     <div className="step-number">3</div>
                     <CardTitle>Token Management</CardTitle>
                     <p style={{ marginBottom: '1.5rem', color: theme.textSecondary }}>
                       Your token has been created! You can perform the following actions:
                     </p>
                     
                     <FormGrid>
                       <ActionButton 
                         onClick={() => handleWalletAction('createAmm')}
                         style={{ backgroundColor: '#0070f3', color: 'white', fontWeight: 'bold' }}
                       >
                         Create AMM Pool 🌊
                       </ActionButton>
                       <ActionButton 
                         onClick={() => handleWalletAction('blackhole')}
                         style={{ backgroundColor: '#ff4d4f', color: 'white', fontWeight: 'bold' }}
                       >
                         Blackhole Issuer 🔒
                       </ActionButton>
                       <ActionButton 
                         onClick={() => handleWalletAction('burnLP')}
                         style={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 'bold' }}
                       >
                         Burn LP Tokens 🔥
                       </ActionButton>
                     </FormGrid>
                     
                     <div style={{ backgroundColor: 'rgba(0, 0, 0, 0.04)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', marginTop: '1rem' }}>
                       <h4 style={{ margin: '0 0 0.5rem 0', color: theme.primary }}>Manual Token Management:</h4>
                       <ul style={{ margin: '0', paddingLeft: '1.5rem' }}>
                         <li><strong>Create AMM Pool 🌊</strong> - Establishes liquidity for your token</li>
                         <li><strong>Blackhole Issuer 🔒</strong> - Prevents issuing more tokens (irreversible!)</li>
                         <li><strong>Burn LP Tokens 🔥</strong> - Removes excess LP tokens from circulation</li>
                       </ul>
                     </div>
                     
                     <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                       <Button primary onClick={() => {
                         if (window.confirm('Are you sure you want to create a new token? This will reset the current token configuration.')) {
                           setTokenCreationStatus('pending');
                           setTokenData({
                             name: '',
                             symbol: '',
                             supply: '1000000',
                             icon: null,
                             banner: null,
                             telegram: '',
                             twitter: '',
                             website: '',
                             description: '',
                             share: 20,
                             additionalLiquidity: '0',
                             receiverAddress: '',
                             ammAmount: '0',
                             issuerAddress: ''
                           });
                         }
                       }}>
                         Create New Token
                       </Button>
                       <Button secondary onClick={() => handleWalletAction('burnTokens')}>
                         Burn Tokens
                       </Button>
                     </div>
                  </FormSection>

                  {tokenCreationStatus === 'pending' && (
                  <Button 
                    primary 
                    large
                    style={{ width: '100%', marginBottom: '2rem' }}
                    onClick={() => performAutomaticAction('createToken')}
                    disabled={!activeWallet || !tokenData.name || !tokenData.symbol || !tokenData.receiverAddress}
                  >
                    {!activeWallet ? 'Connect Wallet First' : 
                     !tokenData.receiverAddress ? 'Select Receiver Wallet' : 
                     parseFloat(tokenData.additionalLiquidity) > 0 ? 'Create Token & Auto-Setup AMM' : 'Create Token'}
                  </Button>
                  )}

                  {tokenCreationStatus === 'completed' && (
                    <FormSection className="completed active">
                      <div className="step-number">4</div>
                      <CardTitle>Token Management & Promotion</CardTitle>
                      <FormGrid>
                        <ActionButton onClick={() => window.location.href = '/'}>
                          View Your Token in Feed
                        </ActionButton>
                        <ActionButton onClick={() => window.open(`https://xrpl.to/token/${tokenData.md5}`, '_blank')}>
                          View on XRPL.to
                        </ActionButton>
                      </FormGrid>
                    </FormSection>
                  )}
                </MainContent>
                
                <Sidebar data-sidebar>
                  <WalletManagementCard>
                    <div className="wallet-header">
                      <h3>Wallet Management</h3>
                      <ButtonGroup>
                        <Button 
                          onClick={generateWallet}
                          disabled={wallets.length >= 2}
                        >
                          Generate Wallet
                        </Button>
                        <Button 
                          onClick={addWallet}
                          disabled={wallets.length >= 2}
                        >
                          Import Wallet
                        </Button>
                      </ButtonGroup>
                </div>

                    {wallets.length === 0 ? (
                      <div className="empty-state">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-wallet"><path d="M21 4H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"></path><path d="M16 2v4"></path><path d="M8 2v4"></path><path d="M21 10H3"></path></svg>
                        <h4>No Wallets Yet</h4>
                        <p>
                          You haven't added any wallets yet. Generate or import one to get started.
                        </p>
                        <Button 
                          primary
                          onClick={generateWallet}
                        >
                          Generate Your First Wallet
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="wallet-instructions">
                          <p>
                            {wallets.length < 2 ? 
                              `You can add ${2 - wallets.length} more wallet${wallets.length === 1 ? '' : 's'}. Click a wallet to select it for token operations.` : 
                              'Maximum number of wallets reached (2).'}
                          </p>
                        </div>

                        {wallets.map((wallet, index) => (
                          <WalletCard
                            key={index}
                            active={activeWallet && activeWallet.address === wallet.address}
                            onClick={() => selectWallet(wallet)}
                          >
                            <div className="wallet-badge">
                              {activeWallet && activeWallet.address === wallet.address ? 'Active' : 'Select'}
                            </div>
                            
                            <WalletInfo>
                              <label>
                                Address:
                                <button 
                                  className="copy-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(wallet.address);
                                    setMessage('Address copied to clipboard');
                                    setTimeout(() => setMessage(null), 2000);
                                  }}
                                >
                                  Copy
                                </button>
                              </label>
                              <p>{wallet.address}</p>
                              
                              <WalletBalance 
                                address={wallet.address}
                              />
                            </WalletInfo>
                            
                            <WalletInfo>
                              <label>
                                Seed:
                                <button 
                                  className="copy-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(wallet.seed);
                                    setMessage('Seed copied to clipboard');
                                    setTimeout(() => setMessage(null), 2000);
                                  }}
                                >
                                  Copy
                                </button>
                              </label>
                              <p className="sensitive-data">{wallet.seed}</p>
                            </WalletInfo>
                            
                            <div className="wallet-actions">
                              <Button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const blob = new Blob(
                                    [JSON.stringify({ address: wallet.address, seed: wallet.seed }, null, 2)],
                                    { type: 'text/plain' }
                                  );
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `xpad_wallet_${wallet.address.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.txt`;
                                  a.setAttribute('aria-label', `Download wallet file for ${wallet.address}`);
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(url);
                                }}
                              >
                                Download
                              </Button>
                              <Button 
                                style={{ 
                                  background: 'rgba(255, 75, 75, 0.1)',
                                  color: '#ff4b4b',
                                  borderColor: 'rgba(255, 75, 75, 0.3)'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Are you sure you want to remove this wallet? This action cannot be undone.')) {
                                    removeWallet(wallet);
                                  }
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </WalletCard>
                        ))}
                        
                        {/* Advanced Configuration inside Wallet Management */}
                        <AdvancedSettingsSection>
                          <div className="section-title">
                            <h4>Wallet Roles Configuration</h4>
                          </div>
                          
                          <div className="wallet-instructions">
                            <p>
                              Select your active wallet as the issuer and your second wallet as the receiver for better token management and security.
                            </p>
                          </div>
                          
                          <div className="advanced-fields">
                            <InputGroup>
                              <label>Issuer Wallet (Active)</label>
                              <StyledSelect
                                value={activeWallet ? activeWallet.address : ''}
                                disabled={true}
                              >
                                <option value={activeWallet ? activeWallet.address : ''}>
                                  {activeWallet ? `${activeWallet.address.substring(0, 8)}...${activeWallet.address.substring(activeWallet.address.length - 8)}` : 'Select a wallet first'}
                                </option>
                              </StyledSelect>
                            </InputGroup>

                            <InputGroup>
                              <label>Receiver Wallet</label>
                              <StyledSelect
                                value={tokenData.receiverAddress || ''}
                                onChange={(e) => setTokenData(prev => ({ ...prev, receiverAddress: e.target.value, issuerAddress: activeWallet ? activeWallet.address : '' }))}
                                disabled={tokenCreationStatus === 'completed' || wallets.length < 2 || !activeWallet}
                              >
                                <option value="">Select Receiver Wallet</option>
                                {wallets
                                  .filter(wallet => activeWallet && wallet.address !== activeWallet.address)
                                  .map((wallet, index) => (
                                    <option key={index} value={wallet.address}>
                                      {wallet.address.substring(0, 8)}...{wallet.address.substring(wallet.address.length - 8)}
                                    </option>
                                  ))}
                              </StyledSelect>
                              {wallets.length < 2 && (
                                <p style={{ color: '#ff4b4b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                                  Please add a second wallet to use as receiver
                                </p>
                              )}
                            </InputGroup>
                          </div>
                        </AdvancedSettingsSection>
                      </>
                    )}
                  </WalletManagementCard>
                </Sidebar>
              </ContentWrapper>
              
              {/* Add social buttons */}
              <SocialButtonsContainer>
                <SocialButton href="https://x.com/Xpad_xrp" target="_blank" bgColor="#000000" title="Follow us on X (Twitter)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </SocialButton>
                <SocialButton href="https://t.me/Xpad_xrpl" target="_blank" bgColor="#0088cc" title="Join our Telegram channel">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </SocialButton>
                
                <div style={{ position: 'fixed', bottom: '5px', right: '5px', opacity: 0.2, zIndex: 9999 }}>
                  <button 
                    onClick={resetTokenCreationStatus}
                    style={{ padding: '5px', fontSize: '10px', cursor: 'pointer', background: '#333', color: 'white', border: 'none', borderRadius: '3px' }}
                    title="Developer: Reset Token Creation Status"
                  >
                    Reset Status
                  </button>
                </div>
              </SocialButtonsContainer>

              {(loading || message) && (
                <LoadingOverlay 
                  message={message} 
                  isLoading={loading}
                />
              )}

              <WalletModal 
                isOpen={showAddWalletModal}
                onClose={() => setShowAddWalletModal(false)}
                onSubmit={handleAddWallet}
              />

              {showErrorModal && (
                <ModalOverlay onClick={() => setShowErrorModal(false)}>
                  <ModalContent onClick={e => e.stopPropagation()}>
                    <ModalTitle>{errorModalContent.title}</ModalTitle>
                    
                    <p style={{ marginBottom: '1.5rem', color: theme.text }}>
                      {errorModalContent.message}
                    </p>
                    
                    {errorModalContent.guide && (
                      <div style={{ 
                        padding: '1rem', 
                        backgroundColor: 'rgba(13, 71, 161, 0.05)', 
                        borderRadius: '12px',
                        marginBottom: '1.5rem',
                        border: `1px solid ${theme.borderLight}`
                      }}>
                        <h4 style={{ color: theme.primary, marginBottom: '0.5rem' }}>
                          {errorModalContent.guide.title}
                        </h4>
                        <p style={{ color: theme.textSecondary, margin: 0 }}>
                          {errorModalContent.guide.content}
                        </p>
                      </div>
                    )}
                    
                    <ButtonGroup style={{ justifyContent: 'center' }}>
                      <Button primary onClick={handleErrorModalAction}>
                      {errorModalContent.guide?.title === 'Domain Verification' ? 'Open Guide' : 'Close'}
                    </Button>
                    </ButtonGroup>
                  </ModalContent>
                </ModalOverlay>
              )}
              
              {showAddWalletModal && (
                <WalletModal
                  onClose={() => setShowAddWalletModal(false)}
                  onAddWallet={handleAddWallet}
                />
              )}
            </AppContainer>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App; 